import React, { useEffect, useRef, useState } from "react";
import TrashIcon from "assets/icon/TrashIcon";
import { useNavigate } from "react-router-dom";
import { DropdownDepositStatus, DropdownStatusList, EmptyString, PageSize, PaymentTypeDeposit, StatusCodeActive, StatusCodeDelete, ViewDateFormat } from "utils/Constant";
import EditIcon from "assets/icon/EditIcon";
import Dropdown from "components/Input/Dropdown";
import { GetBookingStatus, GetMonth, GetPaymentType, GetStatus, money } from "utils/helpers";
import RevisionIcon from "assets/icon/RevisionIcon";
import moment from "moment";
import { MainBodyCard } from "components/Cards/MainBodyCard";
import { PagesTitle } from "components/Cards/PagesTitle";
import { useModal } from "utils/context/ModalContext";
import { DepositReturnService } from "services/DepositReturnService";
import Pagination from "components/Input/PaginationButton";
import { PaymentService } from "services/PaymentService";
import DataTable from "components/DataTable";
import { FilterCard } from "components/Cards/FilterCard";

export default function DepositReturn(){
    let navigate = useNavigate()

    const firstLoad = useRef(true)
    const debounceRef = useRef(null)

    const { showFail, showLoading, hideLoading, showSuccess, confirm } = useModal()

    const { fetchCreateDeposit, fetchDepositList } = DepositReturnService()
    const { fetchDeletePayment } = PaymentService()

    // FILTER
    const [tenantName, setTenantName] = useState(EmptyString)
    const [status, setStatus] = useState(DropdownStatusList[1].value)
    const [depositStatus, setDepositStatus] = useState(DropdownDepositStatus[1].value)

    // DATA
    const [depositList, setDepositList] = useState(EmptyString)

    // GRID
    const [pageIndex, setPageIndex] = useState(0)
    const [totalPages, setTotalPages] = useState(0)

    const columns = [
        {
            key: "rowNumber",
            header: "No",
            headerClassName: "w-16 font-semibold",
            cellClassName: "text-center"
        },
        {
            key: "tenantName",
            header: "Tenant",
            render: (row) => row.tenantName.toUpperCase()
        },
        {
            key: "roomNumber",
            header: "Room",
            render: (row) => row.roomNumber
        },
        {
            key: "bookingStatus",
            header: "Booking Status",
            render: (row) => GetBookingStatus(String(row.bookingStatus))
        },
        {
            key: "amount",
            header: "Amount",
            render: (row) => 'Rp ' + money(row.amount),

        },
        {
            key: "remark",
            header: "Remark",
            // render: (row) => GetMonth(row.paymentPeriodeMonth) + ' ' + row.paymentPeriodeYear
        },
        {
            key: "status",
            header: "Status",
            render: (row) => GetStatus(row.status),
        },
        {
            key: "action",
            header: "Action",
            render: (row) => (
                <div className="flex gap-2">
                    <button className="rounded-full py-1 px-4 bg-black text-white" onClick={() => fullReturnClick(row.paymentId, row.amount)}>
                        <span className="text-xs">Full</span>
                    </button>
                    <button className="rounded-full py-1 px-4 bg-black text-white" onClick={() => splitReturnClick(row.paymentId)}>
                        <span className="text-xs">Split</span>
                    </button>
                    <button className="rounded-full py-1 px-4 bg-black text-white" onClick={() => ownedReturnClick(row.paymentId)}>
                        <span className="text-xs">Owned</span>
                    </button>
                </div>
            ),
        },
    ]

    const handlePageChange = async (newIndex) => {
        try {
            showLoading()

            const res = await fetchDepositList(
                tenantName,
                depositStatus,
                PaymentTypeDeposit,
                status,
                PageSize,
                pageIndex
            )

            if (!res) return

            const totalData = res?.data?.totalData ?? 0;
            const total = Math.max(1, Math.ceil(totalData / PageSize));

            setDepositList(res)

            setTotalPages(total)
            setPageIndex(newIndex)
        } finally {
            hideLoading()
        }
    }

    // BUTTON
    const editBtnClick = (depositReturnId) => {
        navigate("/deposit/form?depositReturnId=" + depositReturnId + "&formType=Edit")
    }
    const deleteBtnClick = async (paymentId, status) => {
        const onConfirm = await confirm()
        if (!onConfirm) return

        try {
            showLoading()

            await fetchDeletePayment(paymentId, status)
            await handlePageChange(pageIndex)
        } finally {
            hideLoading()
        }
    }
    const fullReturnClick = async (paymentId, amount) => {
        const onConfirm = await confirm()
        if (!onConfirm) return

        try {
            showLoading()

            await fetchCreateDeposit(paymentId, amount, "1")
            await handlePageChange(pageIndex)
        } finally {
            hideLoading()
        }
    }
    const ownedReturnClick = async (paymentId) => {
        const onConfirm = await confirm()
        if (!onConfirm) return

        try {
            showLoading()

            await fetchCreateDeposit(paymentId, 0, "2")
            await handlePageChange(pageIndex)
        } finally {
            hideLoading()
        }
    }
    const splitReturnClick = (paymentId) => {
        navigate("/deposit/form?paymentId=" + paymentId + "&formType=Edit")
    }

    useEffect(() => {
        const onLoad = async () => {
            if (firstLoad.current) {
                handlePageChange(0)

                firstLoad.current = false
            }
            else {
                if (debounceRef.current) {
                    clearTimeout(debounceRef.current)
                }
        
                debounceRef.current = setTimeout(() => {
                    handlePageChange(0)
                }, 500)
            }
        }

        onLoad()
    },[tenantName, depositStatus, status])
    
    return(
        <MainBodyCard>
            <PagesTitle title='Deposit' />

            <div className="flex flex-col sm:flex-row gap-5 mt-5">
                <FilterCard title='Tenant Name' width='w-full sm:w-64'>
                    <input className="input" value={tenantName} onChange={(e) => {setTenantName(e.target.value.toUpperCase())}} />
                </FilterCard>

                <FilterCard title='Deposit Status' width='w-full sm:w-32'>
                    <Dropdown className='w-full' value={depositStatus} options={DropdownDepositStatus} onChange={(e) => {setDepositStatus(e.value)}} placeholder={"All"} />
                </FilterCard>

                <FilterCard title='Status' width='w-full sm:w-32'>
                    <Dropdown className='w-full' value={status} options={DropdownStatusList} onChange={(e) => {setStatus(e.value)}} placeholder={"All"} />
                </FilterCard>
            </div>
           
            <div className="mt-5 hidden sm:block">
                <DataTable
                    columns={columns}
                    data={depositList?.data?.listData ?? []}
                    footer={
                        <tr className="h-10">
                            <td colSpan={4} className="text-right pr-2">Total</td>
                            <td colSpan={1} className="text-left pr-2" >{ "Rp " + money(String(depositList?.data?.totalAmount)) }</td>
                        </tr>
                    }
                />
            </div>

            <div className="mt-5 sm:hidden flex flex-col gap-2">
                {depositList?.data?.listData?.map((data, i) => {
                    return(
                        <div className="border rounded-md hover:bg-slate-100 bg-white py-2 px-2 flex justify-between" key={ i }>
                            <div className="text-sm">
                                <div>
                                    <span>{ data.tenantName.toUpperCase() + " - " + data.roomNumber }</span>
                                </div>                                
                                <div>
                                    <span className="font-semibold">{ "Rp " + money(data.amount) }</span>
                                </div>
                                <div className="mt-1 flex gap-2">
                                    <button className="bg-black px-3 py-1 rounded-md flex gap-2" onClick={() => editBtnClick(data.paymentId)}>
                                        <EditIcon className="w-4 fill-white" />
                                        <span className="text-white">Edit</span>
                                    </button>
                                    <button className={`bg-black px-3 py-1 rounded-md flex gap-2 ${ data.status === StatusCodeDelete ? "hidden" : "block" }`} onClick={() => deleteBtnClick(data.paymentId, data.status)}>
                                        <TrashIcon className="w-4 fill-white" />
                                        <span className="text-white">Delete</span>
                                    </button>
                                    <button className={`bg-black px-3 py-1 rounded-md flex gap-2 ${ data.status === StatusCodeActive ? 'hidden' : 'block' }`} onClick={() => deleteBtnClick(data.paymentId, data.status)}>
                                        <RevisionIcon className="w-4 fill-white" />
                                        <span className="text-white">Undelete</span>
                                    </button>
                                </div>
                            </div>
                            <div className="flex flex-col text-right text-sm">
                                <div>
                                    <span>{ moment(data.paymentDate).format(ViewDateFormat) }</span>
                                </div>
                                <div>
                                    <span>{ GetMonth(data.paymentPeriodeMonth) + ' ' + data.paymentPeriodeYear }</span>
                                </div>
                                <div className="flex h-full items-center justify-end">
                                    <span>{ GetPaymentType(String(data.paymentType)) }</span>
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>

            <Pagination pageIndex={pageIndex} totalPages={totalPages} onPageChange={handlePageChange} />
        </MainBodyCard>
    )
}